import { useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useDocsIndex } from '../lib/docsIndex'
import { ApiError, api } from '../lib/api'
import { StaffOnlyError, loadPage, type RenderedPage } from '../lib/page'
import { useAsync } from '../lib/useAsync'
import { useAuth } from '../lib/auth'
import { signInWithSightHub } from '../lib/sso'
import {
  actorEmail,
  actorIsMachine,
  actorLabel,
  canWrite,
  docPath,
  type Actor,
  type DocDetail,
} from '../lib/types'
import { useMeta } from '../lib/seo'
import { Button, Callout, Chip, Spinner, VisibilityBadge } from '../components/ui'
import { OnThisPage } from '../components/OnThisPage'
import { DocActions } from '../components/DocActions'
import { DownloadDoc } from '../components/DownloadDoc'
import { ShareDoc } from '../components/ShareDoc'
import { Comments } from '../components/Comments'
import { SignInChoice } from '../components/SignIn'

/**
 * A fetch's outcome — success or failure — stamped with the doc it describes.
 *
 * `useAsync` holds its previous state until its own effect refires, a commit after the
 * deps change, and an error arriving through the promise's reject channel has nowhere
 * to carry which doc it was about. Resolving with the failure instead of rejecting
 * gives it the same stamp the data gets, so a screen as consequential as "Staff only"
 * can never be drawn from the doc the reader just left.
 */
type Outcome<T> =
  | { docId: number; value: T; error: null }
  | { docId: number; value: null; error: Error }

async function settle<T>(docId: number, run: () => Promise<T>): Promise<Outcome<T>> {
  try {
    return { docId, value: await run(), error: null }
  } catch (e) {
    // An abort lands here too, and that is fine: `useAsync` drops any settled value
    // whose signal was aborted, so a cancelled fetch never reaches the screen.
    return { docId, value: null, error: e instanceof Error ? e : new Error(String(e)) }
  }
}

export function DocPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const { idByPath, pathById, loading: treeLoading } = useDocsIndex()
  const { role, loading: authLoading } = useAuth()

  // Either `/d/126` or the canonical `/glossary/en/rx/od`. The path form resolves
  // through the nav tree, which we already fetch — no extra round trip.
  const byPath = id === undefined
  const docId = byPath ? (idByPath.get(location.pathname) ?? NaN) : Number(id)
  const canonical = pathById.get(docId)

  // Refetch once the role is known: an internal doc 403s for an anonymous read,
  // and the very first render happens before Firebase restores the session.
  const doc = useAsync(
    async (signal): Promise<Outcome<DocDetail>> => {
      // A canonical path resolves through the tree, so `docId` is NaN until the tree
      // arrives. Asking the API about NaN earns a 400 whose error would then be sitting
      // in state, ready to paint under the real doc a moment later.
      if (authLoading || Number.isNaN(docId)) return new Promise<never>(() => {})
      return settle(docId, () => api.doc(docId, signal))
    },
    [docId, role, authLoading],
  )

  /** Metadata, but only once it is *this* URL's metadata. */
  const meta = doc.data?.docId === docId ? doc.data : null
  const d = meta?.value ?? null
  const docError = meta?.error ?? null

  const page = useAsync(
    async (signal): Promise<Outcome<RenderedPage>> => {
      if (!d) return new Promise<never>(() => {})
      return settle(d.id, () => loadPage(d.page, signal))
    },
    // `role` and `authLoading` belong here even though the callback never reads them:
    // an internal body is fetched with the caller's credentials, so who is asking is a
    // real input. Without them, signing in from the "Staff only" screen re-read the
    // metadata, left the body's `StaffOnlyError` untouched, and the button led nowhere.
    // `authLoading` is the one that matters — it clears only after the refreshed token
    // carries the new `role` claim, which is what Storage actually checks.
    [d?.id, d?.page.location, role, authLoading],
  )

  const rendered = page.data?.docId === d?.id ? page.data : null
  const body = rendered?.value ?? null
  const pageError = rendered?.error ?? null

  // An internal doc must never reach an index — not its title, not its description.
  useMeta(
    d
      ? {
          title: d.title,
          description: d.description,
          // Always the keyword path, never `/d/{id}` — two URLs would split the rank.
          path: docPath(d),
          noindex: d.visibility === 'internal',
          tags: d.tags,
          updatedAt: d.updated_at,
          author: actorLabel(d.author),
        }
      : null,
  )

  useEffect(() => {
    if (body) window.scrollTo({ top: 0 })
  }, [body])

  // One page, one URL: an old `/d/{id}` link hands its authority to the canonical one.
  if (!byPath && canonical && canonical !== location.pathname) {
    return <Navigate to={canonical} replace />
  }

  // A path we cannot resolve yet may simply be a tree that has not loaded.
  if (Number.isNaN(docId)) {
    if (byPath && treeLoading) return <Shell><Spinner /></Shell>
    return <NotFound />
  }

  const denied =
    (docError instanceof ApiError && docError.isStaffOnly) || pageError instanceof StaffOnlyError

  if (denied) return <StaffOnly />
  if (docError instanceof ApiError && docError.status === 404) return <NotFound />

  if (docError) {
    return (
      <Shell>
        <Callout tone="danger" title="Could not load this document">
          {docError.message}
        </Callout>
      </Shell>
    )
  }

  // No metadata for *this* URL yet — still loading, or still the doc we just left.
  if (!d) {
    return (
      <Shell>
        <div className="py-16">
          <Spinner />
        </div>
      </Shell>
    )
  }

  return (
    <div className="flex gap-10">
      <article className="min-w-0 flex-1 px-1 py-10 lg:px-8">
        <nav aria-label="Breadcrumb" className="mb-3 flex flex-wrap items-center gap-1.5 font-mono text-xs text-subtle">
          <span>{d.service}</span>
          {/* `topic` is a full path now: doctor-desk/exams → two crumbs. */}
          {d.topic.split('/').filter(Boolean).map((segment) => (
            <span key={segment} className="flex items-center gap-1.5">
              <span aria-hidden>/</span>
              <span>{segment}</span>
            </span>
          ))}
        </nav>

        <div className="mb-2 flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-semibold tracking-tight text-ink">{d.title}</h1>
          <VisibilityBadge visibility={d.visibility} />
        </div>

        {d.description && <p className="text-[17px] text-muted">{d.description}</p>}

        {d.author && (
          <p className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-subtle">
            <span>Written by</span>
            <ActorLabel actor={d.author} />
            {d.last_edited_by && actorEmail(d.last_edited_by) !== actorEmail(d.author!) && (
              <>
                <span>· last edited by</span>
                <ActorLabel actor={d.last_edited_by} />
              </>
            )}
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-b border-line pb-6">
          <div className="flex flex-wrap gap-1.5">
            {d.tags.map((t) => (
              <Link key={t} to={`/search?tag=${encodeURIComponent(t)}`}>
                <Chip label={t} />
              </Link>
            ))}
          </div>
          {/* Five controls no longer fit a phone on one line, so let them wrap. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* No body, no file — but a body still loading only disables the button. */}
            {!pageError && <DownloadDoc doc={d} html={body?.html ?? null} />}
            {/* Keyed so a "Copied" confirmation cannot outlive the doc it was about. */}
            <ShareDoc key={d.id} doc={d} />
            <AuthorControls doc={d} />
            <DocActions docId={d.id} votes={d.votes} favorited={d.favorited} />
          </div>
        </div>

        <div className="mt-8">
          {/* Spin whenever there is nothing else to show — including the commit after
              new metadata lands, before the body effect has even been scheduled. */}
          {!body && !pageError && <Spinner label="Loading page…" />}
          {pageError && !denied && (
            <Callout tone="danger" title="Could not load the page body">
              {pageError.message}
            </Callout>
          )}
          {body && <div className="doc-prose" dangerouslySetInnerHTML={{ __html: body.html }} />}
        </div>

        <p className="mt-12 text-xs text-subtle">
          Last updated{' '}
          <time dateTime={d.updated_at}>{new Date(d.updated_at).toLocaleDateString()}</time>
        </p>

        <Comments docId={d.id} />
      </article>

      <aside className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-56 shrink-0 overflow-y-auto py-10 xl:block">
        {body && <OnThisPage headings={body.headings} />}
      </aside>
    </div>
  )
}

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto max-w-3xl px-1 py-16 lg:px-8">{children}</div>
)

/**
 * Both buttons follow the server's per-document verdict. Older backends omit the
 * flags entirely; then fall back to the coarse role check rather than hiding the
 * controls from everyone.
 */
function AuthorControls({ doc }: { doc: DocDetail }) {
  const { role } = useAuth()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)

  const mayEdit = doc.can_edit ?? canWrite(role)
  const mayDelete = doc.can_delete ?? false
  if (!mayEdit && !mayDelete) return null

  const remove = async () => {
    if (!confirm(`Delete “${doc.title}”? This cannot be undone.`)) return
    setBusy(true)
    try {
      await api.deleteDoc(doc.id)
      navigate('/')
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Could not delete.')
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      {mayEdit && (
        <Link to={`/d/${doc.id}/edit`}>
          <Button size="sm" variant="secondary">
            Edit
          </Button>
        </Link>
      )}
      {mayDelete && (
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void remove()}>
          Delete
        </Button>
      )}
    </div>
  )
}

/** Render the name; the email is a detail, not a byline. A machine gets a badge. */
function ActorLabel({ actor }: { actor: Actor }) {
  const label = actorLabel(actor)
  if (!label) return null

  const email = actorEmail(actor)
  const machine = actorIsMachine(actor)

  return (
    <span className="inline-flex items-center gap-1.5">
      {machine || !email ? (
        <span className="font-medium text-muted">{label}</span>
      ) : (
        <a href={`mailto:${email}`} title={email} className="font-medium text-muted hover:text-ink">
          {label}
        </a>
      )}
      {machine && (
        <span className="rounded-full bg-accent-soft px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-accent-text uppercase">
          Service
        </span>
      )}
    </span>
  )
}

function StaffOnly() {
  const { user, role } = useAuth()
  return (
    <Shell>
      <div className="rounded-[var(--radius-sh)] border border-line bg-surface p-8 text-center shadow-card">
        <div className="mx-auto mb-4 grid size-11 place-items-center rounded-full bg-internal-soft text-internal">
          <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="4" y="10" width="16" height="10" rx="2" />
            <path d="M8 10V7a4 4 0 118 0v3" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-ink">Staff only</h1>
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted">
          This page is internal. Staff open it by signing in with SightHub — a personal
          Google account will not unlock it.
        </p>
        {!user ? (
          <SignInChoice className="mx-auto mt-5 max-w-xs" />
        ) : (
          <>
            <p className="mt-4 font-mono text-xs text-subtle">
              Signed in as role “{role ?? 'unknown'}”.
            </p>
            {/* The way out for staff who signed in with Google: it replaces that session. */}
            <Button variant="primary" className="mt-4" onClick={signInWithSightHub}>
              Sign in with SightHub
            </Button>
            <p className="mt-3 text-xs text-subtle">Not staff? Ask an admin for access.</p>
          </>
        )}
      </div>
    </Shell>
  )
}

function NotFound() {
  return (
    <Shell>
      <h1 className="text-2xl font-semibold text-ink">Document not found</h1>
      <p className="mt-2 text-sm text-muted">
        It may have been moved or unpublished.{' '}
        <Link to="/" className="font-medium text-accent-text hover:underline">
          Back to the wiki
        </Link>
        .
      </p>
    </Shell>
  )
}
